import { Popover } from "@headlessui/react";
import { ArrowPathIcon, ArrowUturnLeftIcon, CheckIcon, ClipboardIcon, NoSymbolIcon } from "@heroicons/react/24/outline";
import { AcademicCapIcon, ArrowTrendingUpIcon, BoltIcon, PauseIcon } from "@heroicons/react/24/solid";
import React from "react";
import { CgBorderStyleDashed } from "react-icons/cg";
import { FaHourglassEnd, FaPersonRunning } from "react-icons/fa6";
import { HiAcademicCap, HiArrowsExpand, HiOutlineMinus, HiXCircle } from "react-icons/hi";

import { PopoverPanel, Tooltip } from "@fronterahealth/frontera-ui-components";

import { CatalightGoalStatus } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";

interface GoalBadgeProps {
  status?: string | null;
  expanded?: boolean;
  onStatusChange?: (status: string) => void;
  statusOptions?: { id: string; label: string }[];
  isReadOnly?: boolean;
}

const GoalBadgeIcon = ({ status }: GoalBadgeProps) => {
  if (status === "New") return BoltIcon;
  if (status === "Inactive") return FaHourglassEnd;
  if (status === "Waiting") return FaHourglassEnd;
  if (status === "Baseline") return CgBorderStyleDashed;
  if (status === "Treatment" || status === "Continued") return ArrowTrendingUpIcon;
  if (status === "Active") return FaPersonRunning;
  if (status === "Mastered") return HiAcademicCap;
  if (status === "Maintenance") return ArrowPathIcon;
  if (status === "Generalization") return HiArrowsExpand;
  if (status === "Hold" || status === "On Hold") return PauseIcon;
  if (status === "Closed") return HiXCircle;
  if (status === "Discontinued") return NoSymbolIcon;
  if (status === "Probe") return ClipboardIcon;
  if (status === "Acquisition") return HiAcademicCap;
  if (status === "Met") return AcademicCapIcon;
  if (status === "Revised") return ArrowUturnLeftIcon;

  return HiOutlineMinus; // default icon
};

const getGoalClassNames = (status: string | null | undefined) => {
  switch (status) {
    case "Inactive":
      return "bg-limestone-300 text-limestone-900";
    case "New":
      return "bg-limestone-50 text-limestone-700";
    case "Baseline":
      return "bg-[#FDF4FF] text-[#A900B7]";
    case "Treatment":
    case "Continued":
      return "bg-blue-50 text-blue-700";
    case "Mastered":
    case "Met":
      return "bg-green-50 text-green-700";
    case "Maintenance":
      return "bg-[#FAF5FF] text-[#8200DA]";
    case "Generalization":
      return "bg-[#CBFBF2] text-[#00786F]";
    case "Hold":
    case "On Hold":
      return "bg-orange-50 text-orange-700";
    case "Closed":
      return "bg-[#4B5563] text-white";
    case "Waiting":
      return "bg-[#edeae7] text-[#72625c]";
    case "Active":
      return "bg-blue-100 text-blue-700";
    case "Discontinued":
      return "bg-red-50 text-red-700";
    case "Probe":
      return "bg-orange-100 text-orange-600";
    case "Acquisition":
      return "bg-blue-100 text-blue-700";
    case "Revised":
      return "bg-blurple-50 text-blurple-700";
    default:
      return "bg-gray-300 text-white";
  }
};

const NOT_SET_LABEL = "Empty";

const GoalBadgeDisplay: React.FC<{ status?: string | null; expanded?: boolean; compact?: boolean }> = ({
  status,
  expanded,
  compact,
}) => {
  const IconComponent = GoalBadgeIcon({ status });
  return (
    <div
      className={`flex flex-row items-center ${compact ? "gap-0.5 px-1" : "gap-1 px-1.5 py-1"} ${getGoalClassNames(status)} rounded-sm text-xs font-semibold`}
    >
      <IconComponent className="h-3 w-3 shrink-0" />
      {expanded && <span className="leading-4">{status ?? NOT_SET_LABEL}</span>}
    </div>
  );
};

export const GoalBadge: React.FC<GoalBadgeProps> = ({
  status,
  expanded,
  onStatusChange,
  statusOptions,
  isReadOnly,
}) => {
  const isEditable = !isReadOnly && !!onStatusChange && !!statusOptions;

  const readableStatusLabel = convertReadableString(status === CatalightGoalStatus.NewGoal ? "New" : status);

  const badge = <GoalBadgeDisplay status={readableStatusLabel} expanded={expanded} />;

  if (!isEditable) {
    return (
      <Tooltip text={status ? readableStatusLabel : NOT_SET_LABEL} showInfoIcon={false} placement="top">
        {badge}
      </Tooltip>
    );
  }

  const popoverContent = (
    <div className="flex flex-col py-1 -m-3">
      {statusOptions.map((option) => (
        <div key={option.id} className="px-2 py-1">
          <Popover.Button
            as="button"
            className="flex w-full items-center justify-between rounded-sm p-1 hover:bg-limestone-50"
            onClick={() => onStatusChange(option.id)}
          >
            <GoalBadgeDisplay status={option.label} expanded compact />
            {option.label === status && <CheckIcon className="h-4 w-4 shrink-0 text-green-600" />}
          </Popover.Button>
        </div>
      ))}
    </div>
  );

  return (
    <PopoverPanel direction="bottom-left" width="w-[180px]" height="max-h-none" content={popoverContent}>
      {badge}
    </PopoverPanel>
  );
};
